import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Map, { Source, Layer } from "react-map-gl";
import DeckGL from "@deck.gl/react";
import type { PickingInfo } from "@deck.gl/core";
import { Center, Loader, Stack, Text } from "@mantine/core";
import { useAppStore } from "../../store/appStore";
import { useDomains, useGridstats } from "../../api/hindcasts";
import {
  makeLocationLayer,
  makeRadiusLayer,
  makeGridstatsLayers,
  makeDomainsPickLayer,
} from "./layers";
import { Colorbar } from "./Colorbar";
import type { TextureDataJSON } from "../../types";

/** Look up the scalar value at [lon, lat] from a 1-channel TextureDataJSON.
 *  Returns null if out of bounds or no data. Row 0 = north (max latitude). */
function lookupGridValue(
  coord: [number, number],
  bounds: [number, number, number, number],
  texture: TextureDataJSON
): number | null {
  const [lon, lat] = coord;
  const [minLon, minLat, maxLon, maxLat] = bounds;
  if (lon < minLon || lon > maxLon || lat < minLat || lat > maxLat) return null;
  const { data, width, height } = texture;
  const col = Math.min(Math.round(((lon - minLon) / (maxLon - minLon)) * (width - 1)), width - 1);
  const row = Math.min(Math.round(((maxLat - lat) / (maxLat - minLat)) * (height - 1)), height - 1);
  const idx = row * width + col;
  return data[idx] ?? null;
}

const MAPBOX_TOKEN = (import.meta.env.VITE_MAPBOX_TOKEN ?? "") as string;
const MAP_STYLE = "mapbox://styles/oceanum/cm3pgvbz2001401rfc5yp3j39";
// Alternative base map with dark continents:
// const MAP_STYLE = "mapbox://styles/oceanum/cmmiddd32007j01sqd46xdhuc";

// Transparent-ocean / opaque-land style — rendered on top of wave layers to
// mask gridded data over land while keeping ocean fully visible.
// const LAND_MASK_STYLE = "mapbox://styles/oceanum/ckr2z9qpd5nyr17rviv40leq8";
// Alternative land mask (pairs with dark continent base map):
const LAND_MASK_STYLE = "mapbox://styles/oceanum/ckr2vwsta5jpk18q78q2xxdza";

interface ViewState {
  longitude: number;
  latitude: number;
  zoom: number;
  pitch: number;
  bearing: number;
}

const DEFAULT_VIEW: ViewState = {
  longitude: 0,
  latitude: 20,
  zoom: 2,
  pitch: 0,
  bearing: 0,
};

interface DeckMapProps {
  onMapClick?: () => void;
}

export function DeckMap({ onMapClick }: DeckMapProps) {
  const { selectedHindcast, lon, lat, radius, layer, showGrids, showParticles, setLocation } = useAppStore();
  const [viewState, setViewState] = useState<ViewState>(DEFAULT_VIEW);
  const [domainTooltip, setDomainTooltip] = useState<{ name: string; x: number; y: number } | null>(null);
  const [valueTooltip, setValueTooltip] = useState<{ label: string; x: number; y: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: domains } = useDomains();
  const { data: gridstats, isFetching: gridstatsFetching } = useGridstats(selectedHindcast);

  // Fly to selected hindcast domain when gridstats loads.
  // For global datasets (lon_extent ≈ 360°) compute the zoom that fits exactly
  // 360° across the current map container width — adapts to any screen size.
  // For regional datasets keep the existing heuristic.
  useEffect(() => {
    if (!gridstats) return;
    const { center_lon, center_lat, lon_extent, lat_extent } = gridstats.meta;
    let zoom: number;
    if (lon_extent >= 355) {
      // deck.gl/Mapbox tile size = 512px; zoom 0 → world = 512px wide
      const mapWidth = containerRef.current?.clientWidth ?? window.innerWidth;
      zoom = Math.log2(mapWidth / 512);
    } else {
      zoom = Math.min(Math.log2(360 / Math.max(lon_extent, lat_extent)) + 1, 8);
    }
    setViewState((vs) => ({ ...vs, longitude: center_lon, latitude: center_lat, zoom }));
  }, [gridstats]);

  // Memoize gridstats layers to avoid recreating Float32Array textures every frame.
  // _animate causes 60fps re-renders; without memo, deck.gl sees a new image prop
  // each frame and resets the ParticleLayer GPU state, killing the animation.
  const gridstatsLayers = useMemo(
    () => (gridstats ? makeGridstatsLayers(gridstats, layer, showParticles) : []),
    [gridstats, layer, showParticles]
  );

  const layers = [];
  if (domains && showGrids) layers.push(makeDomainsPickLayer(domains));
  layers.push(...gridstatsLayers);
  if (lon != null && lat != null) {
    layers.push(makeLocationLayer(lon, lat));
    layers.push(makeRadiusLayer(lon, lat, radius));
  }

  const handleHover = useCallback((info: PickingInfo) => {
    const name = (info.object as { properties?: { name?: string } } | null)
      ?.properties?.name;
    if (name && info.x != null && info.y != null) {
      setDomainTooltip({ name, x: info.x, y: info.y });
      setValueTooltip(null);
      return;
    }
    setDomainTooltip(null);

    if (!gridstats || info.coordinate == null || info.x == null) {
      setValueTooltip(null);
      return;
    }

    const coord = info.coordinate as [number, number];
    const landVal = lookupGridValue(coord, gridstats.bounds, gridstats.land_mask);
    if (landVal === 1) {
      setValueTooltip(null);
      return;
    }

    if (layer === "wave") {
      const val = lookupGridValue(coord, gridstats.bounds, gridstats.hs);
      if (val == null) { setValueTooltip(null); return; }
      setValueTooltip({ label: `Hs: ${val.toFixed(2)} m`, x: info.x, y: info.y });
    } else if (layer === "wind" && gridstats.wspd) {
      const val = lookupGridValue(coord, gridstats.bounds, gridstats.wspd);
      if (val == null) { setValueTooltip(null); return; }
      setValueTooltip({ label: `Wind: ${val.toFixed(1)} m/s`, x: info.x, y: info.y });
    } else {
      setValueTooltip(null);
    }
  }, [gridstats, layer]);

  // Clear tooltips when grids are hidden
  useEffect(() => {
    if (!showGrids) { setDomainTooltip(null); setValueTooltip(null); }
  }, [showGrids]);

  const handleClick = useCallback(
    (info: PickingInfo) => {
      onMapClick?.();
      if (!selectedHindcast) return;
      if (!info.coordinate || info.coordinate[0] == null) return;
      const [clickLon, clickLat] = info.coordinate as [number, number];
      setLocation(clickLon, clickLat);
    },
    [selectedHindcast, setLocation, onMapClick]
  );

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%", height: "100%" }}>
      <DeckGL
        viewState={viewState}
        onViewStateChange={({ viewState: vs }) => setViewState(vs as ViewState)}
        _animate={!!gridstats}
        controller
        layers={layers}
        onClick={handleClick}
        onHover={handleHover}
        getTooltip={null}
        getCursor={({ isDragging }: { isDragging: boolean }) =>
          isDragging ? "grabbing" : selectedHindcast ? "crosshair" : "grab"
        }
      >
        <Map
          mapStyle={MAP_STYLE}
          mapboxAccessToken={MAPBOX_TOKEN}
          reuseMaps
        />
      </DeckGL>
      {/* Land mask rendered on top of all deck.gl layers: transparent ocean,
          opaque land — same approach as the previous Dash app overlay.
          Domain outlines are added here as Mapbox GL layers so they render
          above the land mask and remain visible over coastlines. */}
      <Map
        longitude={viewState.longitude}
        latitude={viewState.latitude}
        zoom={viewState.zoom}
        pitch={viewState.pitch}
        bearing={viewState.bearing}
        mapStyle={LAND_MASK_STYLE}
        mapboxAccessToken={MAPBOX_TOKEN}
        interactive={false}
        style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
        reuseMaps
      >
        {domains && showGrids && (
          <Source id="domains" type="geojson" data={domains}>
            {/* Subtle dark fill */}
            <Layer
              id="domains-fill"
              type="fill"
              paint={{ "fill-color": "#000000", "fill-opacity": 0.08 }}
            />
            {/* Dark outer glow for contrast against the wave colourmap */}
            <Layer
              id="domains-glow"
              type="line"
              layout={{ "line-join": "round", "line-cap": "round" }}
              paint={{
                "line-color": "#000000",
                "line-width": 8,
                "line-blur": 6,
                "line-opacity": 0.45,
              }}
            />
            {/* Crisp white inner line */}
            <Layer
              id="domains-line"
              type="line"
              layout={{ "line-join": "round", "line-cap": "round" }}
              paint={{
                "line-color": "#ffffff",
                "line-width": 1.5,
                "line-opacity": 0.85,
              }}
            />
          </Source>
        )}
      </Map>
      {domainTooltip && (
        <div
          style={{
            position: "absolute",
            left: domainTooltip.x + 12,
            top: domainTooltip.y - 8,
            background: "rgba(0,0,0,0.65)",
            color: "#fff",
            fontSize: 12,
            fontWeight: 500,
            padding: "4px 8px",
            borderRadius: 4,
            pointerEvents: "none",
            whiteSpace: "nowrap",
            zIndex: 20,
          }}
        >
          {domainTooltip.name}
        </div>
      )}
      {valueTooltip && (
        <div
          style={{
            position: "absolute",
            left: valueTooltip.x + 12,
            top: valueTooltip.y - 8,
            background: "rgba(0,0,0,0.65)",
            color: "#fff",
            fontSize: 12,
            fontWeight: 500,
            padding: "4px 8px",
            borderRadius: 4,
            pointerEvents: "none",
            whiteSpace: "nowrap",
            zIndex: 20,
          }}
        >
          {valueTooltip.label}
        </div>
      )}
      {selectedHindcast && gridstatsFetching && (
        <Center
          pos="absolute"
          style={{ inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 30, pointerEvents: "none" }}
        >
          <Stack align="center" gap="sm">
            <Loader color="white" size="lg" />
            <Text c="white" fw={500} size="sm">
              Loading {selectedHindcast}…
            </Text>
          </Stack>
        </Center>
      )}
      {gridstats && (
        <Colorbar
          palette={layer === "wave" ? gridstats.meta.wave_palette : gridstats.meta.wind_palette}
          ticks={
            layer === "wave"
              ? [0, 0.5, 1, 1.5, 2, 6, 9]
              : [0, 3, 5, 10, 15, 20, 30]
          }
          unit={layer === "wave" ? "m" : "m/s"}
        />
      )}
    </div>
  );
}
